import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { message, Typography, Spin, Button } from "antd";
import AIGeneratorFullScreen from "./Step0AIGenerator";
import JobPostHeader from "./components/JobPostHeader";
import Step1BasicDetails from "./Steps/Step1BasicDetails";
import Step2Requirements from "./Steps/Step2Requirements";
import Step3InterviewRounds from "./Steps/Step3InterviewRounds";
import Step4ManualScreening from "./Steps/Step4HRScreening";
import Step5PostAssessment from "./Steps/Step5PostAssessment";
import ReviewModal from "./Steps/Step6Review/components/ReviewModal";
import { JobDescriptionEntity } from "../../Entities/JobDescription";
import { useJobPostForm } from "./hooks/useJobPostForm";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { transformJobPostToFormData } from "./utils/formTransformers";
import { ensureDefaultRounds } from "./Steps/Step3InterviewRounds/utils/defaultRounds";
import { transformFormToStep3 } from "./Steps/Step3InterviewRounds/utils/formTransformers";
import { JobPostMode } from "./types";
import jobDescriptionService from "../../Service/job.description.service";
import interviewService from "../../Service/interview.service";
import { useLoader } from "../../Context/LoaderContext";
import useOrganization from "../../Hooks/useOrganization";
import {
  validateStep1Complete,
  validateStep2Complete,
} from "./hooks/useFormValidation";
import { clearUserDrafts, generateDraftId, parseStorageKey } from "./utils/storageUtils";
import AuthService from "../../Service/auth.service";
import { useSubscriptionLimit } from "./hooks/useSubscriptionLimit";
import {
  getUserFriendlyErrorMessage,
  logError,
  withRetry,
} from "./utils/errorHandling";
import { DEFAULT_PASS_PERCENTAGE } from "./utils/constants";
import SubscriptionWarning from "../../Components/SubscriptionWarning";
import "./index.scss";

const AddJobPostV3: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setLoading, setLoadingMessage } = useLoader();
  const { org } = useOrganization();

  // Subscription limit checking
  const {
    checkLimit,
    limit,
    isLoading: subscriptionLoading,
    hasValidData,
  } = useSubscriptionLimit();

  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const limitCheckedRef = useRef(false);

  // Get mode, jobPostId, and other params from URL params
  // Support both 'mode' (preferred) and 'action' (backward compatibility)
  const modeParam =
    searchParams.get("mode") || searchParams.get("action") || "new";
  const jobPostId =
    searchParams.get("id") || searchParams.get("dupJobPost") || undefined;
  const sectionKey = searchParams.get("sectionKey") || undefined;
  const mode = (
    ["edit", "clone"].includes(modeParam) ? modeParam : "new"
  ) as JobPostMode;

  /**
   * Unique draft ID for this session's storage key.
   * - edit mode  → jobPostId (one slot per job post being edited)
   * - new/clone  → URL param ?draftId=xxx, or a generated UUID when absent
   *
   * Initialised once so it stays stable across re-renders.
   * The URL is updated (replace) on mount when the param is missing so the
   * same draft is restored if the user refreshes or shares the URL.
   */
  const [draftId, setDraftId] = useState<string>(() => {
    if (mode === "edit") return jobPostId || "default";
    return searchParams.get("draftId") || generateDraftId();
  });

  // Sync draftId into the URL on first mount for new/clone sessions that
  // didn't already have it (so page-refresh restores the correct draft).
  useEffect(() => {
    if (mode === "edit") return;
    if (searchParams.get("draftId") === draftId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("draftId", draftId);
        return next;
      },
      { replace: true },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  /**
   * Map old accordion sectionKey to new stepper step number
   * Old accordion keys: "2"=Basic Details, "3"=Requirements, "4"=HR Requirements,
   *                     "5"=Interview Rounds, "6"=Interviewer, "7"=Interview Guests
   * New stepper steps: 1=Basic Details, 2=Requirements, 3=Interview Rounds,
   *                    4=Job Alignment, 5=Post Assessment, 6=Review
   */
  const mapSectionKeyToStep = (key: string | undefined): number | null => {
    if (!key) return null;

    const sectionKeyMap: Record<string, number> = {
      "2": 1, // Basic Details → Step 1
      "3": 2, // Requirements → Step 2
      "4": 4, // HR Requirements → Step 4 (Job Alignment)
      "5": 3, // Interview Rounds → Step 3
      "6": 5, // Interviewer → Step 5 (Post Assessment)
      "7": 5, // Interview Guests → Step 5 (Post Assessment)
    };

    return sectionKeyMap[key] ?? null;
  };

  // Form instance (single source of truth)
  const { form, saveStatus } = useJobPostForm({
    mode,
    jobPostId,
    draftId,
  });

  // Local state for UI
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isLoadingJobPost, setIsLoadingJobPost] = useState<boolean>(false);
  const [isGenerated, setIsGenerated] = useState<boolean>(false);
  const [localPrompt, setLocalPrompt] = useState<string>("");
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false);

  // Cleanup drafts on user logout
  useEffect(() => {
    const authService = AuthService.getInstance();
    const handleSignOut = () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        clearUserDrafts(currentUser._id, currentUser.orgId);
      }
    };

    authService.subscribe(AuthService.EVENTS.USER_SIGNED_OUT, handleSignOut);

    return () => {
      authService.unsubscribe(
        AuthService.EVENTS.USER_SIGNED_OUT,
        handleSignOut,
      );
    };
  }, []);

  // Check subscription limits on mount (only for 'new' mode)
  useEffect(() => {
    console.log('[AddJobPostV3] Limit-check effect triggered:', {
      mode,
      subscriptionLoading,
      hasValidData,
      limitAlreadyChecked: limitCheckedRef.current,
      limit,
      calledAt: new Date().toISOString(),
    });

    // Only check for 'new' mode, not 'edit' or 'clone'
    if (mode !== "new") {
      console.log('[AddJobPostV3] Skipping — not new mode');
      return;
    }

    // Only run the limit check once on initial load.
    // Re-running after publish would incorrectly block because the
    // subscription measurement gets incremented by the just-created post.
    // Mark as checked as soon as we have valid data (even if still loading)
    // so a second effect trigger (when loading flips false) doesn't re-run it.
    if (limitCheckedRef.current) {
      console.log('[AddJobPostV3] Skipping — already checked (limitCheckedRef=true)');
      return;
    }

    // If we already have valid data, mark as checked immediately to prevent
    // a second effect run (loading→false) from re-entering this block.
    if (hasValidData) {
      limitCheckedRef.current = true;
    }

    // Wait for subscription data to load before actually evaluating
    if (subscriptionLoading) {
      console.log('[AddJobPostV3] Skipping — subscription still loading');
      return;
    }

    // Check limit once data is available
    if (hasValidData) {
      const limitCheck = checkLimit(mode);

      console.log('[AddJobPostV3] checkLimit result:', limitCheck);

      if (limitCheck.shouldBlock) {
        console.warn('[AddJobPostV3] BLOCKING — showing upgrade popup');
        setIsBlocked(true);
        setShowUpgradePopup(true);

        // Show warning message
        if (limitCheck.reason) {
          message.warning(limitCheck.reason, 5);
        }
      }
    } else {
      console.log('[AddJobPostV3] Skipping — hasValidData is false');
    }
  }, [mode, subscriptionLoading, hasValidData, checkLimit]);

  // Watch form values to track completion and changes (declared once)
  const formValues = form.watch();

  // Total steps is 6 (Step 0-5, Review is now a modal)
  const totalSteps = 6;

  // Step navigation
  const {
    currentStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    isNavigating,
  } = useStepNavigation(form, totalSteps);

  // Update completion state when form values change
  useEffect(() => {
    const step1Data = formValues.step1;
    const step2Data = formValues.step2;

    const newCompletedSteps: number[] = [];

    if (step1Data && validateStep1Complete(step1Data)) {
      if (!completedSteps.includes(1)) {
        newCompletedSteps.push(1);
      }
    }

    if (step2Data && validateStep2Complete(step2Data)) {
      if (!completedSteps.includes(2)) {
        newCompletedSteps.push(2);
      }
    }

    if (newCompletedSteps.length > 0) {
      setCompletedSteps([...completedSteps, ...newCompletedSteps]);
    }

    // Track unsaved changes
    setHasUnsavedChanges(true);
  }, [formValues]);

  // Set isGenerated to true immediately when in edit/clone mode to prevent Step 0 flash
  useEffect(() => {
    if ((mode === "edit" || mode === "clone") && jobPostId) {
      setIsGenerated(true);
    }
  }, [mode, jobPostId]);

  // Consume pre-generated onboarding data for fresh-start users (mode=new only).
  // UserOnboarding stores the /generate response in sessionStorage so we can skip
  // Step 0 without creating a draft that counts against the job post limit.
  useEffect(() => {
    if (mode !== "new") return;

    const raw = sessionStorage.getItem("onboarding_generated_job_post");
    if (!raw) return;

    try {
      const generated = JSON.parse(raw);
      sessionStorage.removeItem("onboarding_generated_job_post");

      // Format the raw AI response into a JobDescriptionEntity-like shape
      // using the same structure as useAIGeneration.formatJobPostResponse
      const jobPost = {
        _id: "",
        title: generated.title || "Generated Job Post",
        description: generated.description || "",
        position: generated.position || "Position Title",
        requirements: (generated.requirements || []).map((req: any, index: number) => ({
          id: index + 1,
          topic: req.topic || "",
          description: req.description || "",
          mustMatch: req.mustMatch ?? true,
        })),
        additionalRequirements: [],
        numberOfOpenings: generated.numberOfOpenings || 1,
        numberOfApplications: 0,
        numberOfQualifiedApplications: 0,
        numberOfPendingInterviews: 0,
        numberOfHiredApplications: 0,
        numberOfRejectedApplications: 0,
        interviewers: [],
        isActive: true,
        orgId: "",
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: "",
        ctc: undefined,
        noticePeriod: undefined,
        endDate: "",
        isProfileMatchRequired: true,
        interviewRounds: [],
        interviewGuests: [],
        slug: "",
        interviewRoundExpiry: 5,
        skills: Array.isArray(generated.skills)
          ? generated.skills.filter((s: any) => typeof s === "string" && s.trim().length > 0)
          : [],
      };

      // Re-use the same handleGenerate path to populate the form and move to Step 1
      handleGenerate(jobPost as JobDescriptionEntity);
    } catch (err) {
      console.error("[AddJobPostV3] Failed to restore onboarding_generated_job_post:", err);
      sessionStorage.removeItem("onboarding_generated_job_post");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount — mode is stable at this point

  // Initialize default interview rounds in create mode (new/clone) when form is created
  // This ensures rounds are initialized even if user never visits Step 3
  // Uses centralized ensureDefaultRounds utility to maintain consistency
  useEffect(() => {
    const step3Data = formValues.step3;
    const formMode = formValues.mode as "new" | "edit" | "clone" | undefined;
    const isEditMode = formMode === "edit";

    // Only initialize in create mode (new/clone) when rounds are empty
    if (
      !isEditMode &&
      (!step3Data ||
        !step3Data.interviewRounds ||
        step3Data.interviewRounds.length === 0)
    ) {
      // Use centralized utility to ensure default rounds (Profile Matching and Overall Assessment)
      const defaultRounds = ensureDefaultRounds([], true, false);
      const step3StoreData = transformFormToStep3(
        defaultRounds,
        formValues.step3?.isProfileMatchRequired ?? true
      );
      form.setValue("step3", step3StoreData, { shouldDirty: false });
    }
  }, [formValues.mode, formValues.step3, form]);

  // Load job post data when in edit/clone mode
  useEffect(() => {
    let isMounted = true;

    const loadJobPostData = async () => {
      // Only load if we have a jobPostId and are in edit/clone mode
      if (!jobPostId || (mode !== "edit" && mode !== "clone")) {
        return;
      }

      // Prevent multiple simultaneous loads
      if (isLoadingJobPost) {
        return;
      }

      setIsLoadingJobPost(true);
      setLoading(true);
      setLoadingMessage("Loading job post...");

      try {
        // Fetch job post, round details, and generic rounds in parallel.
        // - getAllInterviewRoundsSpecificToJob: round documents whose _id values match
        //   the interviewRoundId values stored in the job post (for title lookup).
        // - getGenericRoundsOnly: to identify the real ObjectId of the "Job Alignment"
        //   template so we can normalize it back to the 'job-alignment' string before
        //   transforming (mapJobDescriptionToStep4 looks for the string, not the ObjectId).
        const [jobPost, roundDetails, genericRounds] = await Promise.all([
          withRetry(
            () => jobDescriptionService.getJobDescriptionById(jobPostId),
            3,
            1000,
            (error) => {
              const status = error?.response?.status;
              return !status || (status >= 500 && status !== 404);
            },
          ),
          interviewService
            .getAllInterviewRoundsSpecificToJob(jobPostId)
            .catch(() => [] as any[]),
          interviewService
            .getGenericRoundsOnly()
            .catch(() => [] as any[]),
        ]);

        if (!isMounted) return;

        if (jobPost) {
          // Identify the real DB ObjectId of the "Job Alignment" generic round.
          // When saving, 'job-alignment' is replaced with the ObjectId; we need to
          // reverse this so mapJobDescriptionToStep4 can find the round correctly.
          const jobAlignmentGenericRound = (genericRounds as any[]).find((r: any) => {
            const title = (r.title || r.roundName || '').toLowerCase().replace(/\s+/g, '');
            return title === 'jobalignment';
          });
          const jobAlignmentObjectId = jobAlignmentGenericRound?._id;

          // Normalize job post: replace job-alignment ObjectId with the canonical
          // 'job-alignment' string so mapJobDescriptionToStep4 can find it correctly.
          // (When saving, transformFormDataToPayload replaces 'job-alignment' with the
          // real DB ObjectId, which breaks the round lookup on reload.)
          let normalizedJobPost = jobPost;
          if (jobAlignmentObjectId && jobPost.interviewRounds?.length > 0) {
            const hasJobAlignment = jobPost.interviewRounds.some(
              (r: any) => r.interviewRoundId === jobAlignmentObjectId,
            );
            if (hasJobAlignment) {
              normalizedJobPost = {
                ...jobPost,
                interviewRounds: jobPost.interviewRounds.map((r: any) => {
                  if (r.interviewRoundId === jobAlignmentObjectId) {
                    return { ...r, interviewRoundId: 'job-alignment' };
                  }
                  return r;
                }),
              };
            }
          }

          // In clone mode, create independent copies of custom rounds in the DB.
          // This prevents modifications to the clone's rounds from affecting the original job.
          if (mode === "clone" && normalizedJobPost.interviewRounds?.length > 0) {
            const SPECIAL_IDS = new Set(['profile-match', 'job-alignment', 'overall-assessment']);
            const idMapping = new Map<string, string>();

            await Promise.allSettled(
              normalizedJobPost.interviewRounds
                .filter((r: any) => r.interviewRoundId && !SPECIAL_IDS.has(r.interviewRoundId))
                .map(async (round: any) => {
                  const detail = (roundDetails as any[]).find((d: any) => d._id === round.interviewRoundId);
                  if (!detail) return;
                  const d = detail as any;
                  try {
                    const copied = await interviewService.createInterviewRound({
                      roundName: detail.title || d.roundName || '',
                      customRound: {
                        _id: '',
                        question_type: d.customRound?.question_type || detail.answerType || 'mcq',
                        custom_ai_prompt: d.customRound?.custom_ai_prompt || detail.questionGenerationPrompt || '',
                        evaluation_criteria: d.customRound?.evaluation_criteria || detail.evaluationPrompt || '',
                        question_id: 2,
                      },
                      roundNumber: '1',
                      category: detail.category || ['Custom'],
                    });
                    if (copied?._id) {
                      idMapping.set(round.interviewRoundId, copied._id);
                      (roundDetails as any[]).push({ ...detail, _id: copied._id });
                    }
                  } catch (err) {
                    console.error('[Clone] Failed to copy round, using original:', round.interviewRoundId, err);
                  }
                }),
            );

            if (idMapping.size > 0) {
              normalizedJobPost = {
                ...normalizedJobPost,
                interviewRounds: normalizedJobPost.interviewRounds.map((r: any) => {
                  const newId = idMapping.get(r.interviewRoundId);
                  return newId ? { ...r, interviewRoundId: newId } : r;
                }),
              };
            }
          }

          // Transform job post data to form format
          const formData = transformJobPostToFormData(normalizedJobPost, mode);

          // Eagerly populate _displayMetadata for each round using the fetched round details.
          // This avoids the async enrichment in Step3 that was causing "Untitled Round" on
          // initial render. roundDetails[].title matches jobPost.interviewRounds[].interviewRoundId.
          if (roundDetails.length > 0 && formData.step3?.interviewRounds) {
            formData.step3.interviewRounds = formData.step3.interviewRounds.map(
              (round: any) => {
                const detail = roundDetails.find(
                  (r: any) => r._id === round.interviewRoundId,
                );
                if (!detail) return round;
                const detailAny = detail as any;
                return {
                  ...round,
                  _displayMetadata: {
                    ...round._displayMetadata,
                    title:
                      detail.title ||
                      detailAny.roundName ||
                      round._displayMetadata?.title,
                    description:
                      detail.description || round._displayMetadata?.description,
                    questionGenerationPrompt:
                      detailAny.customRound?.custom_ai_prompt ||
                      detail.questionGenerationPrompt ||
                      round._displayMetadata?.questionGenerationPrompt,
                    evaluationPrompt:
                      detailAny.customRound?.evaluation_criteria ||
                      detail.evaluationPrompt ||
                      round._displayMetadata?.evaluationPrompt,
                    answerType:
                      detailAny.customRound?.question_type ||
                      detail.answerType ||
                      round._displayMetadata?.answerType,
                    category:
                      detail.category || round._displayMetadata?.category,
                    _id: detail._id || round._displayMetadata?._id,
                    _fetchedAt: Date.now(),
                  },
                };
              },
            );
          }

          // Remove currentStep from formData - it should be managed by the hook, not the form
          // This prevents conflicts between form state and hook state
          const { currentStep: _, ...formDataWithoutStep } = formData;

          // Reset form with loaded data (without currentStep) - use a single batch update
          form.reset(formDataWithoutStep, { keepDefaultValues: false });

          // Mark as generated (to skip Step 0) - already set above, but ensure it's true
          setIsGenerated(true);
          setHasUnsavedChanges(true);

          // Map sectionKey to step
          let targetStep = 1;
          if (sectionKey) {
            const mappedStep = mapSectionKeyToStep(sectionKey);
            if (mappedStep !== null) {
              targetStep = mappedStep;
            }
          }

          // Use requestAnimationFrame to ensure DOM is ready before navigation
          requestAnimationFrame(() => {
            if (!isMounted) return;

            // Navigate to target step - ensure we're not already on that step
            // This prevents the "can't move forward/backward" issue
            if (targetStep !== currentStep) {
              goToStep(targetStep);
            }
          });
        } else {
          if (isMounted) {
            message.error(
              `The job post you're looking for doesn't exist or has been deleted.`,
            );
            navigate("/job-posts");
          }
        }
      } catch (error) {
        if (!isMounted) return;

        // Log error with context
        logError("AddJobPostV3.loadJobPostData", error, {
          jobPostId,
          mode,
        });

        // Show user-friendly error message
        const errorMessage = getUserFriendlyErrorMessage(
          error,
          "Failed to load job post. Please try again or check if the job post exists.",
        );
        message.error(errorMessage, 5);
        navigate("/job-posts");
      } finally {
        if (isMounted) {
          setIsLoadingJobPost(false);
          setLoading(false);
          setLoadingMessage("");
        }
      }
    };

    loadJobPostData();

    return () => {
      isMounted = false;
    };
  }, [jobPostId, mode]); // Reduced dependencies to prevent re-runs

  // Navigate to sectionKey-mapped step when sectionKey is present and form is ready
  // This handles the case when sectionKey is provided but we're not in edit/clone mode
  // OR when sectionKey changes after initial load
  useEffect(() => {
    // Only run if we're not loading job post and we have a sectionKey
    // Skip if we're in edit/clone mode (handled by the loadJobPostData effect above)
    if (
      sectionKey &&
      isGenerated &&
      !isLoadingJobPost &&
      mode !== "edit" &&
      mode !== "clone"
    ) {
      let targetStep = mapSectionKeyToStep(sectionKey);

      if (targetStep !== null && targetStep !== currentStep) {
        // Use setTimeout to ensure form is fully initialized
        const stepToNavigate = targetStep; // Store in const to satisfy TypeScript
        setTimeout(() => {
          goToStep(stepToNavigate);
        }, 100);
      }
    }
  }, [
    sectionKey,
    isGenerated,
    isLoadingJobPost,
    currentStep,
    goToStep,
    form,
    formValues,
    mode,
  ]);

  /**
   * Handle AI generation success
   * Shows header and footer, resets prompt, moves to next step
   * Preserves ATS info (appJobId, appType) from step0 if present
   */
  const handleGenerate = (jobPost: JobDescriptionEntity) => {
    console.log("Generated:", jobPost);

    // Get current ATS info before reset (if imported from ATS)
    const currentStep0 = form.getValues("step0") as
      | { prompt?: string; appJobId?: string; appType?: string }
      | undefined;
    const atsInfo =
      currentStep0?.appJobId && currentStep0?.appType
        ? { appJobId: currentStep0.appJobId, appType: currentStep0.appType }
        : null;

    // Transform job post to form data
    const formData = transformJobPostToFormData(jobPost, mode);

    // Preserve ATS info if it exists (from ATS import)
    if (atsInfo) {
      formData.step0 = {
        ...formData.step0,
        ...atsInfo,
      };
      console.log(
        "[AddJobPostV3] Preserving ATS info after generation:",
        atsInfo,
      );
    }

    form.reset(formData);

    // Verify ATS info is preserved after reset
    const step0AfterReset = form.getValues("step0") as
      | { prompt?: string; appJobId?: string; appType?: string }
      | undefined;
    console.log(
      "[AddJobPostV3] Step0 after reset (should have ATS info):",
      step0AfterReset,
    );

    setIsGenerated(true);
    setLocalPrompt(""); // Clear local prompt

    // Clear prompt but preserve ATS info
    if (atsInfo) {
      form.setValue("step0", {
        ...step0AfterReset,
        prompt: "", // Clear prompt but keep ATS fields
        ...atsInfo, // Ensure ATS info is still there
      });
      const step0AfterPromptClear = form.getValues("step0") as
        | { prompt?: string; appJobId?: string; appType?: string }
        | undefined;
      console.log(
        "[AddJobPostV3] Step0 after clearing prompt (should still have ATS info):",
        step0AfterPromptClear,
      );
    } else {
      form.setValue("step0.prompt", "");
    }

    goToStep(1); // Move to first form step
    setHasUnsavedChanges(true);
  };

  /**
   * Handle ATS import
   * Stores ATS metadata in form state (will be persisted in drafts automatically)
   */
  const handleATSImport = (appJobId: string, appType: string) => {
    // This is handled in Step0AIGenerator, but we keep this for consistency
    // The form state is already updated by Step0AIGenerator
    console.log("[AddJobPostV3] ATS import handled, info stored in form state");
  };

  /**
   * Handle draft restoration
   * Loads draft from localStorage and restores form state.
   * Also re-keys the persistence hook to the restored draft's storage slot so
   * subsequent auto-saves update that slot instead of the current session's slot.
   */
  const handleRestoreDraft = (draft: {
    storageKey: string;
    mode: JobPostMode;
    jobPostId?: string;
    step?: number;
  }) => {
    try {
      const saved = localStorage.getItem(draft.storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === "object") {
          form.reset(parsed);

          // Re-key the persistence hook to the restored draft's unique slot so
          // future auto-saves write back to the right localStorage entry.
          const keyInfo = parseStorageKey(draft.storageKey);
          if (keyInfo?.draftId && keyInfo.draftId !== draftId) {
            setDraftId(keyInfo.draftId);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set("draftId", keyInfo.draftId);
                return next;
              },
              { replace: true },
            );
          }

          setIsGenerated(true);
          goToStep(draft.step || 1);
          setHasUnsavedChanges(true);
          message.success("Draft restored successfully");
        } else {
          message.error(
            "The saved draft appears to be corrupted. Please create a new job post.",
          );
        }
      } else {
        message.error(
          "This draft is no longer available. It may have been deleted or expired.",
        );
      }
    } catch (error) {
      console.error("Error restoring draft:", error);
      message.error(
        "Unable to restore your draft. Please try again or create a new job post.",
      );
    }
  };

  /**
   * Handle prompt change in footer
   * Updates prompt state for regeneration
   */
  const handlePromptChange = (value: string) => {
    setLocalPrompt(value);
    form.setValue("step0.prompt", value); // Also save to form
    setHasUnsavedChanges(true);
  };

  /**
   * Handle AI regeneration from footer
   * Triggers new AI generation with modified prompt
   */
  const handleRegenerate = () => {
    if (!localPrompt.trim() || isGenerating) {
      return;
    }
    setIsGenerating(true);
    // TODO: Implement regeneration logic
    setTimeout(() => {
      setIsGenerating(false);
      setHasUnsavedChanges(true);
    }, 2000); // Simulate API call
  };

  /**
   * Handle previous step navigation
   */
  const handlePrevious = async () => {
    if (currentStep > 0) {
      if (currentStep === 1) {
        // Going back to AI generator - don't clear draft, keep it for recovery
        goToStep(0);
        setIsGenerated(false);
        setHasUnsavedChanges(false);
      } else {
        await goToPreviousStep();
      }
    } else {
      // Already at step 0, navigate back to job posts list - don't clear draft
      navigate("/job-posts");
    }
  };

  const handleSubscriptionWarningClose = () => {
    setShowUpgradePopup(false);
    if (isBlocked) {
      navigate("/job-posts");
    }
  };

  /**
   * Handle next step navigation
   */
  const handleNext = async () => {
    // On Step 5, open Review modal instead of going to next step
    if (currentStep === 5) {
      setIsReviewModalVisible(true);
      return;
    }

    // Step 4: Handle "Skip & Proceed" logic (currentStep === 4 is Job Alignment)
    if (currentStep === 4) {
      const step4Data = form.getValues("step4");
      const hasJobAlignment = step4Data?.jobAlignment != null;
      const hasManualQuestions =
        (step4Data?.manualScreeningQuestions?.length ?? 0) > 0;

      // If no jobAlignment and no questions -> "Skip & Proceed"
      // Clear jobAlignment and set aiGenerated to false in the payload
      if (!hasJobAlignment && !hasManualQuestions) {
        const currentStep4 = form.getValues("step4");
        form.setValue(
          "step4",
          {
            ...currentStep4,
            jobAlignment: undefined, // Job Alignment is skipped
          },
          { shouldDirty: true },
        );
      } else if (!hasJobAlignment && hasManualQuestions) {
        // If no jobAlignment but has questions, create jobAlignment with aiGenerated: false
        const currentStep4 = form.getValues("step4");
        form.setValue(
          "step4",
          {
            ...currentStep4,
            jobAlignment: {
              isAIGenerated: false,
              passPercentage: DEFAULT_PASS_PERCENTAGE,
              isMustQualify: true,
            },
          },
          { shouldDirty: true },
        );
      }
    }

    // Navigate to next step if not on the last step
    if (currentStep < totalSteps - 1) {
      await goToNextStep();
    }
  };

  // Step titles for dynamic navigation labels
  const stepTitles = useMemo(() => {
    return [
      "AI Generator", // Step 0
      "Basic Details", // Step 1
      "Requirements", // Step 2
      "Assessments", // Step 3
      "Job Alignment", // Step 4
      "Post Assessment", // Step 5
      // Review is now a modal, not a step
    ];
  }, []);

  // Step 4 is optional - no auto-initialization needed
  // Job Alignment will be created only when user enables AI switch or adds questions

  // Render current step
  const renderStep = () => {
    // If blocked due to subscription limit, show message instead of form
    if (isBlocked && mode === "new") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            padding: "40px 20px",
            textAlign: "center",
          }}
        >
          <Typography.Title level={3} style={{ marginBottom: 16 }}>
            Job Post Limit Reached
          </Typography.Title>
          <Typography.Paragraph
            style={{ fontSize: 16, marginBottom: 24, maxWidth: 600 }}
          >
            You have reached your limit of {limit} active job post
            {limit === 1 ? "" : "s"}. Please upgrade your subscription to create
            more job posts.
          </Typography.Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate("/job-posts")}
          >
            Go to Job Posts
          </Button>
        </div>
      );
    }

    if (!isGenerated && currentStep === 0) {
      return (
        <AIGeneratorFullScreen
          onGenerate={handleGenerate}
          isVisible={true}
          onRestoreDraft={handleRestoreDraft}
          onATSImport={handleATSImport}
        />
      );
    }

    if (isGenerated) {
      const footerProps = {
        currentStep,
        totalSteps,
        prompt: localPrompt,
        isGenerating,
        onPromptChange: handlePromptChange,
        onGenerate: handleRegenerate,
        onPrevious: handlePrevious,
        onNext: handleNext,
        isPreviousDisabled: isNavigating,
        isNextDisabled: isNavigating,
        isNavigating,
        saveStatus,
        stepTitles,
      };

      switch (currentStep) {
        case 1:
          return <Step1BasicDetails {...footerProps} />;
        case 2:
          return <Step2Requirements {...footerProps} />;
        case 3:
          return <Step3InterviewRounds {...footerProps} />;
        case 4:
          // Step 4 is always Job Alignment
          return <Step4ManualScreening {...footerProps} />;
        case 5:
          // Step 5 is Post Assessment
          return <Step5PostAssessment {...footerProps} />;
        // Review is now a modal, not a step
        default:
          return <Step1BasicDetails {...footerProps} />;
      }
    }

    return null;
  };

  if (isLoadingJobPost) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="add-job-post-v3-container">
        {/* Header - Always visible (including Step 0), but stepper only for steps > 0 */}
        <JobPostHeader
          isVisible={true}
          mode={mode}

          currentStep={currentStep}
          isDirty={hasUnsavedChanges}
          onStepChange={goToStep}
          stepTitles={stepTitles}
          totalSteps={totalSteps}
          completedSteps={completedSteps}
          saveStatus={saveStatus}
          formData={formValues}
          handlePrevious={handlePrevious}
        />

        {/* Main Content Area - Scrollable */}
        <main
          className={`main-content ${
            currentStep === 0 ? "main-content--step0" : ""
          }`}
        >
          {renderStep()}
        </main>

        {/* Review Modal */}
        {isReviewModalVisible && (
          <ReviewModal
            visible={isReviewModalVisible}
            onClose={() => setIsReviewModalVisible(false)}
            orgName={org?.orgName || ""}
            onStepChange={goToStep}
            draftId={draftId}
          />
        )}

        {/* Upgrade Popup - shown when limit is reached on initial load */}
        {showUpgradePopup && (
          <SubscriptionWarning
            open={showUpgradePopup}
            handleClose={handleSubscriptionWarningClose}
            title="Limit Reached"
            type="job-post-limit"
          />
        )}
      </div>
    </FormProvider>
  );
};

export default AddJobPostV3;
